/**
 * Live mode's turn rules on the client, pure so `bun test` proves them and
 * both clients read one copy (docs/plans/os-voice-conversation.md, The
 * conversation contract, rules 7, 8 and 17):
 *
 *   pauseOf           whether what the person is saying pauses OS's voice
 *   duckOf            whether OS's voice is turned down
 *   loudRoomOf        whether the room is loud, and the threshold it raises
 *   selfEchoOf        whether the "person" is OS's own voice coming back
 *   echoSwitchStep    a call dropped to half duplex by an echo, and back
 *                     to full with earphones
 *   osTurnStep        OS's playing turn: its lines, and when it stopped
 *   halfDuplexHeard   whether a mic frame is sent on a client-gated call
 */
import { echoKeysOf, longestCommonRun } from './echo-keys';
import { normaliseSaid, phraseSetOf, saidPhraseOf, STOP_PHRASES, YES_NO_PHRASES } from './stop-words';

const STOPS = phraseSetOf(STOP_PHRASES);
const YES_NO = phraseSetOf(YES_NO_PHRASES);

/**
 * Sounds a transcriber writes for what is not a word: a cough, a laugh,
 * "hmm". Bracketed notes (`[cough]`, `(laughs)`, `*sniff*`) go whole.
 */
const NOT_WORDS = new Set(['hmm', 'hm', 'mm', 'mmm', 'um', 'umm', 'uh', 'uhh', 'ah', 'ahem', 'er', 'erm', 'khm', 'huh']);

/** The words of a line, without the transcriber's notes and the sounds that are not words. */
export const liveWordsOf = (text: string): readonly string[] =>
  normaliseSaid(text.replace(/\[[^\]]*\]|\([^)]*\)|\*[^*]*\*/g, ' '))
    .split(' ')
    .filter((word) => word !== '' && !NOT_WORDS.has(word));

export type PauseOptions = {
  /** The room is loud (`loudRoomOf`): OS is ducked, and never paused on word count alone. */
  readonly loud?: boolean;
};

/**
 * Whether the person's words, as they come (`partial`), pause OS's voice.
 * `playing` is the line OS is saying now, or null when it is silent: nothing
 * to pause then. A stop or a plain yes/no pauses as one word ("ruko", "haan");
 * otherwise two words that are not OS's own coming back do. A cough, a "hmm"
 * or one stray word does not. In a loud room only a stop or a yes/no does.
 */
export const pauseOf = (partial: string, playing: string | null, options: PauseOptions = {}): boolean => {
  if (playing === null) return false;
  const words = liveWordsOf(partial);
  if (words.length === 0) return false;
  const phrase = saidPhraseOf(words.join(' '));
  if (STOPS.has(phrase) || YES_NO.has(phrase)) return true;
  if (options.loud === true) return false;
  const echo = new Set(liveWordsOf(playing));
  return words.filter((word) => !echo.has(word)).length >= 2;
};

/** What the client's voice activity detector reads, as the `vad` frame carries it. */
/** The client's voice activity; `near` is null when the client cannot judge it (a simulator, a mic with automatic gain). */
export type LiveVad = { readonly speaking: boolean; readonly near: boolean | null; readonly level: number };

/**
 * Whether OS's voice is ducked: while anyone speaks (within 150 ms, the
 * client's job), and for as long as the room is loud (rule 8).
 */
export const duckOf = (vad: LiveVad, loud: boolean): boolean => loud || vad.speaking;

/** How long a level has to hold before the room counts as loud, in samples (about 100 ms each: three seconds). */
export const LOUD_ROOM_SAMPLES = 30;
/** The share of those samples that must sit above the floor. */
export const LOUD_ROOM_SHARE = 0.8;
/** How far above the room's own level the raised threshold sits. */
export const LOUD_ROOM_MARGIN = 0.1;

/**
 * Whether the room is loud: a speech-like level held over the floor for
 * `LOUD_ROOM_SAMPLES` (`levels` is the newest run of levels, 0–1, oldest
 * first). A loud room raises the speech threshold to its own middle level
 * plus a margin, so only a voice above the room counts; a quiet one keeps
 * the floor.
 */
export const loudRoomOf = (levels: readonly number[], floor: number): { readonly loud: boolean; readonly threshold: number } => {
  const recent = levels.slice(-LOUD_ROOM_SAMPLES);
  if (recent.length < LOUD_ROOM_SAMPLES) return { loud: false, threshold: floor };
  const above = recent.filter((level) => level > floor).length;
  if (above / recent.length < LOUD_ROOM_SHARE) return { loud: false, threshold: floor };
  const sorted = [...recent].sort((a, b) => a - b);
  const middle = sorted[Math.floor(sorted.length / 2)] ?? floor;
  return { loud: true, threshold: Math.min(1, Math.max(floor, middle + LOUD_ROOM_MARGIN)) };
};

/** The run of OS's keys, in order, that makes a partial its echo. */
export const SELF_ECHO_MIN_RUN = 3;

/**
 * Whether what the transcriber hears while OS plays is OS's own voice coming
 * back through the room (rule 17), against `osWords`, OS's words of the turn
 * playing (`osTurnWordsOf`; null outside it, and then nothing is an echo).
 * Compared as keys (`echoKeysOf`): an echo when three or more of the
 * partial's keys run in OS's order, or when a short line (two or three
 * keys) the transcriber has settled (`final`) is all OS's. Phase 0 caught
 * 13 of 13 echo utterances within the utterance this way, with no hit on a
 * person's own (0 of 18). A short partial still growing is not judged: a
 * person cutting in often opens on OS's own name or figure ("Sita Devi
 * nahi, Gita Devi"), and grows into words OS never said, where an echo grows
 * into a run of OS's. A fired detector drops the call to half duplex.
 */
export const selfEchoOf = (partial: string, osWords: string | null, final = false): boolean => {
  if (osWords === null) return false;
  const heard = echoKeysOf(partial);
  const said = echoKeysOf(osWords);
  if (heard.length === 0 || said.length === 0) return false;
  if (longestCommonRun(heard, said) >= SELF_ECHO_MIN_RUN) return true;
  if (!final) return false;
  const bag = new Set(said);
  return heard.length >= 2 && heard.length <= SELF_ECHO_MIN_RUN && heard.every((key) => bag.has(key));
};

/**
 * A call's echo switch: `echo` once the detector fired (the call runs half
 * duplex: the client gates its mic while OS plays, the Interrupt chip shows),
 * `earphonesNote` while "Earphones on. Talk over me any time." shows after
 * earphones brought it back to full.
 */
export type EchoSwitch = { readonly echo: boolean; readonly earphonesNote: boolean };

export const NEW_ECHO_SWITCH: EchoSwitch = { echo: false, earphonesNote: false };

export type EchoSwitchEvent =
  /** The transcriber's words while OS's turn plays (`osWords`), on a call `open` gave full duplex. */
  | { readonly type: 'partial'; readonly text: string; readonly final?: boolean; readonly osWords: string | null; readonly playing: boolean; readonly openFull: boolean }
  /** Earphones came or went (a route change; the web's `devicechange`). */
  | { readonly type: 'earphones'; readonly on: boolean; readonly openFull: boolean }
  /** The earphones note went. */
  | { readonly type: 'note_done' };

/**
 * One step of the switch, and the `duplex` frame it sends: `half` when the
 * detector fires on a full-duplex call, `full` when earphones come in on a
 * call the echo dropped (the detector is armed again with them).
 */
export const echoSwitchStep = (held: EchoSwitch, event: EchoSwitchEvent): { readonly switch: EchoSwitch; readonly send: 'half' | 'full' | null } => {
  switch (event.type) {
    case 'partial':
      if (!event.openFull || held.echo || !event.playing || !selfEchoOf(event.text, event.osWords, event.final === true)) return { switch: held, send: null };
      return { switch: { echo: true, earphonesNote: false }, send: 'half' };
    case 'earphones':
      if (!event.on || !event.openFull || !held.echo) return { switch: held, send: null };
      return { switch: { echo: false, earphonesNote: true }, send: 'full' };
    case 'note_done':
      return { switch: held.earphonesNote ? { ...held, earphonesNote: false } : held, send: null };
  }
};

/**
 * How long after OS's voice stops its turn still counts: the transcriber's
 * partials trail the audio, and the room's echo of the last words with them.
 */
export const OS_TURN_TAIL_MS = 1_500;

/**
 * OS's turn as the client plays it: every line of it whole under its id
 * (the server streams each line whole each time), whether it plays now, and
 * when it last stopped. The voice streams its words ahead of the speaker, so
 * a line belongs to the turn it arrived in until the turn is over (stopped
 * longer than the tail); a line after that starts the next turn.
 */
export type OsTurn = {
  readonly lines: readonly { readonly id: string; readonly text: string }[];
  readonly playing: boolean;
  readonly endedAtMs: number;
};

export const NEW_OS_TURN: OsTurn = { lines: [], playing: false, endedAtMs: -Infinity };

export type OsTurnEvent =
  | { readonly type: 'line'; readonly id: string; readonly text: string; readonly atMs: number }
  | { readonly type: 'playing'; readonly on: boolean; readonly atMs: number };

const turnOver = (turn: OsTurn, atMs: number): boolean => !turn.playing && atMs - turn.endedAtMs > OS_TURN_TAIL_MS;

export const osTurnStep = (turn: OsTurn, event: OsTurnEvent): OsTurn => {
  if (event.type === 'playing') {
    if (event.on === turn.playing) return turn;
    return event.on ? { ...turn, playing: true } : { ...turn, playing: false, endedAtMs: event.atMs };
  }
  if (turn.lines.some((line) => line.id === event.id)) {
    return { ...turn, lines: turn.lines.map((line) => (line.id === event.id ? { id: line.id, text: event.text } : line)) };
  }
  const line = { id: event.id, text: event.text };
  return turnOver(turn, event.atMs) && turn.lines.length > 0 ? { ...turn, lines: [line] } : { ...turn, lines: [...turn.lines, line] };
};

/** OS's words of the turn playing, or just played within the tail; null outside it. */
export const osTurnWordsOf = (turn: OsTurn, atMs: number): string | null => {
  if (turn.lines.length === 0 || turnOver(turn, atMs)) return null;
  return turn.lines.map((line) => line.text.trim()).filter((text) => text !== '').join(' ');
};

/**
 * Whether a mic frame is sent on a client-gated (half duplex) call: not muted
 * by the person, not while OS's voice plays, and not inside the echo's grace
 * after it. Today's `micHeardOf` without the server's hold.
 */
export const halfDuplexHeard = ({ muted, playing, quietUntil, now }: { readonly muted: boolean; readonly playing: boolean; readonly quietUntil: number; readonly now: number }): boolean =>
  !muted && !playing && now >= quietUntil;
